import { useState, useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import moment from 'moment';
import apiClient from 'services/apiClient';
import { decryptRestrictedMessage } from 'utils/restrictedMessageCrypto';
import { buildReplyToFromMessage, resolveReplyWamid } from './replyUtils';
import { DEFAULT_LOCATION_LABEL } from './locationUtils';
import { getMediaTypeFromFileName } from './mediaKindUtils';

/**
 * Manages the full message send pipeline for a chat conversation.
 *
 * Owns reply state, optimistic message appending, background API sends,
 * keyword-blocked message handling, media sends, and location sends.
 *
 * @param {object} params
 * @param {string|number} params.id                          - Active contact ID
 * @param {string}        params.api                         - Base API URL
 * @param {string}        params.authToken                   - Bearer token for new-chat endpoint
 * @param {string|number} params.lastReportID                - Last incoming message ID (for report linking)
 * @param {string}        params.infoNumber                  - Contact WhatsApp number (to field)
 * @param {Function}      params.updateMessagesForContact    - LiveChatContext updater
 * @param {Function}      params.appendOptimistic            - LiveChatContext optimistic appender
 * @param {Function}      params.clearSelectedMedia          - Clears the media queue after send
 * @param {import('rc-field-form').FormInstance} params.form - Ant Design form instance
 * @param {Array}         params.selectedMedia               - Current media queue from useSelectedMedia
 * @param {Function}      params.setDisable                  - Setter for send-button disabled state
 * @param {Function}      params.scrollToBottom              - Scrolls the chat body to the bottom
 */
const useMessageSender = ({
  id,
  api,
  authToken,
  lastReportID,
  infoNumber,
  updateMessagesForContact,
  appendOptimistic,
  clearSelectedMedia,
  form,
  selectedMedia,
  setDisable,
  scrollToBottom,
}) => {
  const [replyingTo, setReplyingTo] = useState(null);

  // Reset reply state whenever the active contact changes
  useEffect(() => {
    setReplyingTo(null);
  }, [id]);

  const handleReply = useCallback((msg) => {
    setReplyingTo(msg);
  }, []);

  const clearReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  const replyingToPreview = useMemo(
    () => (replyingTo ? buildReplyToFromMessage(replyingTo) : null),
    [replyingTo]
  );

  // ─── Optimistic patch ──────────────────────────────────────────────────────

  const patchOptimisticMessage = useCallback((tempId, patch) => {
    updateMessagesForContact(id, (prev) =>
      prev.map((msg) =>
        msg._tempId === tempId ? { ...msg, ...patch } : msg
      )
    );
  }, [id, updateMessagesForContact]);

  // ─── Keyword-blocked handler ───────────────────────────────────────────────

  const handleKeywordBlockedSend = useCallback(
    async (tempId, data, chatPayload) => {
      const restrictionText = data?.error || 'Message blocked by keyword restriction.';
      const originalMessage =
        (await decryptRestrictedMessage(data?.original_message)) ||
        chatPayload?.original_message ||
        chatPayload?.message ||
        '';

      patchOptimisticMessage(tempId, {
        status: 'failed',
        _errorMsg: restrictionText,
        text: restrictionText,
        original_message: originalMessage,
        keyword_blocked: true,
        ...(data?.chat_id ? { id: data.chat_id } : {}),
      });

      if (data?.chat_id) {
        message.error(restrictionText);
        return;
      }

      try {
        await apiClient.post(
          `${api}new-chat`,
          {
            ...chatPayload,
            message: restrictionText,
            original_message: originalMessage,
            out_report_id: data?.out_report_id || null,
            message_id: null,
          },
          {
            headers: { Authorization: 'Bearer ' + authToken },
          }
        );
      } catch (persistError) {
        console.error('Failed to persist keyword-blocked message', persistError);
      }

      message.error(restrictionText);
    },
    [api, authToken, patchOptimisticMessage]
  );

  // ─── Background send ───────────────────────────────────────────────────────

  const sendInBackground = useCallback(
    (tempId, metaPayload, chatPayload) => {
      (async () => {
        const sendPayload = {
          ...metaPayload,
          report_id: chatPayload.report_id,
          chat_type: chatPayload.type,
          reply_id: chatPayload.reply_id,
          quoted_message_wamid: chatPayload.quoted_message_wamid,
        };

        try {
          const res = await apiClient.post('messaging/send', sendPayload);
          if (!res.data.ok || !res.data.wamid) {
            if (res.data?.keyword_blocked) {
              await handleKeywordBlockedSend(tempId, res.data, chatPayload);
              return;
            }

            const errMsg = res.data?.error || 'Message send failed';
            patchOptimisticMessage(tempId, { status: 'failed', _errorMsg: errMsg });
            message.error(errMsg);
            return;
          }

          const wamid = res.data.wamid;
          patchOptimisticMessage(tempId, {
            status: 'sent',
            id: wamid,
            message_id: wamid,
          });

          const reportPayload = {
            ...chatPayload,
            message_id: wamid,
            out_report_id: res.data.out_report_id || null,
          };

          await apiClient.post(`${api}new-chat`, reportPayload, {
            headers: { Authorization: 'Bearer ' + authToken },
          });
        } catch (error) {
          const data = error?.response?.data;
          if (data?.keyword_blocked) {
            await handleKeywordBlockedSend(tempId, data, chatPayload);
            return;
          }

          const errMsg = data?.error || data?.message || 'Failed to send message';
          patchOptimisticMessage(tempId, { status: 'failed', _errorMsg: errMsg });
          message.error(errMsg);
        }
      })();
    },
    [api, authToken, handleKeywordBlockedSend, patchOptimisticMessage]
  );

  // ─── Single message sender ─────────────────────────────────────────────────

  const sendSingleMessage = useCallback(
    (metaPayload, messageText, messageType, displayMeta = null, replyMeta = null) => {
      const now = new Date().toISOString();
      const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const isAudioMessage = messageType === 'audio';
      const isLocationMessage = messageType === 'location';
      const optimisticText = isAudioMessage
        ? ''
        : isLocationMessage
          ? (displayMeta?.display_text || displayMeta?.location_name || messageText)
          : displayMeta?.file_name || messageText;

      appendOptimistic(id, {
        _tempId: tempId,
        type: messageType || 'text',
        text: optimisticText,
        file_name: displayMeta?.file_name || null,
        media_url:
          displayMeta?.media_url
          || (isAudioMessage ? messageText : null),
        latitude: displayMeta?.latitude ?? null,
        longitude: displayMeta?.longitude ?? null,
        location_name: displayMeta?.location_name || null,
        location_address: displayMeta?.location_address || null,
        from: 'me',
        msgType: messageType || 'text',
        status: 'pending',
        id: tempId,
        message_id: null,
        reply_to: replyMeta?.reply_to ?? null,
        quoted_message_wamid: replyMeta?.quoted_message_wamid ?? null,
        mesgTime: moment(now).format('h:mm A'),
        created_at: now,
      });

      const chatPayload = {
        type: messageType,
        report_id: lastReportID,
        message: messageType === 'audio'
          ? messageText || displayMeta?.media_url || ''
          : messageText,
      };
      if (displayMeta?.file_name) chatPayload.file_name = displayMeta.file_name;
      if (displayMeta?.media_url) chatPayload.media_url = displayMeta.media_url;
      if (displayMeta?.latitude != null) chatPayload.latitude = displayMeta.latitude;
      if (displayMeta?.longitude != null) chatPayload.longitude = displayMeta.longitude;
      if (displayMeta?.location_name) chatPayload.location_name = displayMeta.location_name;
      if (displayMeta?.location_address) chatPayload.location_address = displayMeta.location_address;
      if (replyMeta?.reply_id) chatPayload.reply_id = replyMeta.reply_id;

      sendInBackground(tempId, metaPayload, chatPayload);
    },
    [appendOptimistic, id, lastReportID, sendInBackground]
  );

  // ─── Reply meta builder ────────────────────────────────────────────────────

  const buildReplyMeta = useCallback(() => {
    if (!replyingTo) {
      return null;
    }

    const replyWamid = resolveReplyWamid(replyingTo);
    if (!replyWamid) {
      return null;
    }

    return {
      reply_id: replyWamid,
      quoted_message_wamid: replyWamid,
      reply_to: buildReplyToFromMessage(replyingTo),
    };
  }, [replyingTo]);

  // ─── onSend (form submit handler) ─────────────────────────────────────────

  const onSend = useCallback((values) => {
    const replyMeta = buildReplyMeta();
    if (replyingTo && !replyMeta?.reply_id) {
      message.warning('Cannot reply to this message.');
      return;
    }

    const caption = values.newMsg?.trim() || '';

    const readyMedia = selectedMedia.filter(
      (item) => item.status !== 'uploading' && item.id
    );

    if (selectedMedia.some((item) => item.status === 'uploading')) {
      message.warning('Please wait for uploads to finish');
      return;
    }

    if (readyMedia.length > 0) {
      updateMessagesForContact(id, (prev) =>
        prev.filter((item) => item.msgType !== 'pending_message')
      );

      for (let index = 0; index < readyMedia.length; index += 1) {
        const item = readyMedia[index];
        const mediaType = getMediaTypeFromFileName(item.name);
        if (!mediaType) {
          message.error(`Unsupported file type: ${item.name || 'file'}`);
          continue;
        }

        const mediaPayload = { id: item.id };
        if (index === 0 && caption) {
          mediaPayload.caption = caption;
        }
        if (mediaType === 'document' && item.name) {
          mediaPayload.filename = item.name;
        }

        const isDocument = mediaType === 'document';
        const isAudio = mediaType === 'audio';
        const persistedMessage = isDocument
          ? JSON.stringify({ name: item.name, url: item.link })
          : item.link;

        sendSingleMessage(
          {
            messaging_product: 'whatsapp',
            to: infoNumber,
            type: mediaType,
            [mediaType]: mediaPayload,
            ...(index === 0 && replyMeta?.reply_id
              ? { context: { message_id: replyMeta.reply_id } }
              : {}),
          },
          persistedMessage,
          mediaType,
          isDocument
            ? { file_name: item.name, media_url: item.link }
            : isAudio
              ? { media_url: item.link }
              : null,
          index === 0 ? replyMeta : null
        );
      }

      form.resetFields();
      clearSelectedMedia();
      clearReply();
      scrollToBottom();
      setDisable(true);
      return;
    }

    const payload = {
      messaging_product: 'whatsapp',
      to: infoNumber,
      type: 'text',
      text: {
        preview_url: false,
        body: values.newMsg,
      },
      ...(replyMeta?.reply_id
        ? { context: { message_id: replyMeta.reply_id } }
        : {}),
    };

    sendSingleMessage(payload, values.newMsg, 'text', null, replyMeta);
    form.resetFields();
    clearReply();
    scrollToBottom();
    setDisable(true);
  }, [
    buildReplyMeta,
    clearReply,
    clearSelectedMedia,
    form,
    id,
    infoNumber,
    replyingTo,
    selectedMedia,
    sendSingleMessage,
    setDisable,
    scrollToBottom,
    updateMessagesForContact,
  ]);

  // ─── Location send ─────────────────────────────────────────────────────────

  const sendLocationMessage = useCallback((location) => {
    const replyMeta = buildReplyMeta();
    if (replyingTo && !replyMeta?.reply_id) {
      message.warning('Cannot reply to this message.');
      return;
    }

    const label = [location.name, location.address]
      .filter(Boolean)
      .join(' - ') || DEFAULT_LOCATION_LABEL;
    const persistedMessage = JSON.stringify({
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
      name: location.name || '',
      address: location.address || '',
    });

    sendSingleMessage(
      {
        messaging_product: 'whatsapp',
        to: infoNumber,
        type: 'location',
        location: {
          latitude: Number(location.latitude),
          longitude: Number(location.longitude),
          ...(location.name ? { name: location.name } : {}),
          ...(location.address ? { address: location.address } : {}),
        },
        ...(replyMeta?.reply_id
          ? { context: { message_id: replyMeta.reply_id } }
          : {}),
      },
      persistedMessage,
      'location',
      {
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
        location_name: location.name || '',
        location_address: location.address || '',
        display_text: label,
      },
      replyMeta
    );
    clearReply();
    scrollToBottom();
  }, [buildReplyMeta, clearReply, infoNumber, replyingTo, scrollToBottom, sendSingleMessage]);

  return {
    replyingTo,
    replyingToPreview,
    handleReply,
    clearReply,
    onSend,
    sendLocationMessage,
  };
};

export default useMessageSender;
